import WithLessonUtilsHOC, { WithLessonUtilsProp } from '@hocs/WithLessonUtils'
import { Clear, Tick, Minus } from '@berlitz/icons'
import Pagination from '@berlitz/pagination'
import Spacer from '@berlitz/spacer'
import Table, { TableLoader } from '@components/Table'
import { ColumnProps } from '@components/Table/interface'
import { Pill } from '@components/PedagogicalTable/style'
import { LessonHistory } from '@interfaces/LessonHistory'
import { Material as MaterialInterface } from '@interfaces/Material'
import { GroupClassListItem } from '@interfaces/MySchedule'
import paginate from '@utils/helpers/paginate'
import find from 'lodash/find'
import get from 'lodash/get'
import * as React from 'react'
import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl'
import { Col, Row } from 'react-styled-flexboxgrid'
import { Section } from '../Layout/style'
import { RecordTableProps } from './interface'
import {
  Box,
  ButtonContainer,
  Label,
  List,
  OutlineButton,
  PaginationContainer,
  RecordWrapper,
  SelectLevel,
  StyledButton,
  StyledH2,
  StyledSelect,
} from './style'

interface TableListProp {
  columns: Array<ColumnProps<LessonHistory>>
  lessonHistory: LessonHistory[]
}

const TableList: React.FunctionComponent<TableListProp> = ({ columns, lessonHistory }) => {
  const displayItem = 10
  const numberPages = Math.ceil(lessonHistory.length / displayItem)
  const [activePage, setActivePage] = React.useState(1)
  const [data, setData] = React.useState([])

  React.useEffect(() => {
    setData(paginate(lessonHistory, displayItem, activePage))
  }, [lessonHistory, displayItem, activePage])

  const setPaginatedData = (page: number, passedHistory?: LessonHistory[]) => {
    const lh = passedHistory || lessonHistory
    setActivePage(page)
    setData(paginate(lh, displayItem, page))
  }
  return (
    <>
      <Table columns={columns} dataSource={data} />
      {lessonHistory.length > displayItem && (
        <PaginationContainer>
          <Pagination
            activePage={activePage}
            numberPages={numberPages}
            onChange={(pageNumber) => {
              setPaginatedData(pageNumber)
            }}
            prevLabel="Back"
            nextLabel="Next"
          />
        </PaginationContainer>
      )}
    </>
  )
}

const RecordTable: React.FunctionComponent<InjectedIntlProps & RecordTableProps & WithLessonUtilsProp> = ({
  intl,
  lessonHistory = [],
  setLessonHistory,
  instructor,
  selectedStudent,
  options,
  setStudent,
  students,
  loading,
  displayLessonsBtn,
  title = 'Pedagogical record',
  ...props
}) => {
  const [ahColView, setahColView] = React.useState(false)
  const [showMyLessons, setShowMyLessons] = React.useState(false)
  const attendanceHomeCol = [
    {
      title: intl.formatMessage({ id: 'Date' }),
      dataIndex: 'date',
      key: 'date',
      align: 'left',
      render: (text: string) => <Label>{text}</Label>,
    },
    {
      title: intl.formatMessage({ id: 'Attendance' }),
      dataIndex: 'attendance',
      key: 'attendance',
      align: 'left',
      render: (node: React.ReactNode, record: any) => (!record.isRegistered ? '-' : <Label>{node}</Label>),
    },
    {
      title: intl.formatMessage({ id: 'Out of class practice' }),
      dataIndex: 'goals',
      key: 'goals',
      align: 'left',
      render: (_text: string, record: any) => {
        const homework = (record && record.homework) || {
          status: '',
          title: '',
        }

        if (!record.isRegistered || (record.isLastHomeworkDone === null && !homework.title)) {
          return '-'
        }

        let HiddenPill = (
          <Pill spaceRight success empty>
            <Tick />
          </Pill>
        )

        if (homework.status === 'Completed') {
          HiddenPill = (
            <Pill spaceRight success>
              <Tick />
            </Pill>
          )
        } else if (homework.status === 'Pending') {
          HiddenPill = (
            <Pill spaceRight>
              <Clear />
            </Pill>
          )
        }

        return (
          <>
            {homework.title && (
              <>
                {HiddenPill} <span>{homework.title}</span>
              </>
            )}
          </>
        )
      },
    },
  ]

  let defaultColumns = [
    {
      title: intl.formatMessage({ id: 'Date' }),
      dataIndex: 'date',
      key: 'date',
      align: 'left',
      width: '15%',
      // eslint-disable-next-line react/display-name
      render: (text: string, record: LessonHistory) => <Label status={record.attendance}>{text}</Label>,
    },
    {
      title: intl.formatMessage({ id: 'Material' }),
      dataIndex: 'materials',
      key: 'materials',
      align: 'left',
      width: '25%',
      // eslint-disable-next-line react/display-name
      render: (materials: MaterialInterface[], record: LessonHistory) => (
        <>
          {!record.isRegistered
            ? '-'
            : materials.map(
                ({ MaterialName, ChapterName, Page, Point }, index) =>
                  MaterialName && (
                    <List key={`${MaterialName}-${index}`} showBullet={materials.length > 1} status={record.attendance}>
                      <li>
                        <div>
                          <Label status={record.attendance}>
                            {record.attendance === 'Absent' ? (
                              <>
                                ({MaterialName} | {ChapterName} | {intl.formatMessage({ id: 'Page' })} {Page}{' '}
                                {Point && `- ${Point}`})
                              </>
                            ) : (
                              <>
                                {MaterialName} | {ChapterName} | {intl.formatMessage({ id: 'Page' })} {Page}{' '}
                                {Point && `- ${Point}`}
                              </>
                            )}
                          </Label>
                          <Spacer size="xs" />
                        </div>
                      </li>
                    </List>
                  )
              )}
        </>
      ),
    },
    {
      title: intl.formatMessage({ id: 'Lesson goal' }),
      dataIndex: 'description',
      key: 'description',
      align: 'left',
      // eslint-disable-next-line react/display-name
      render: (text: string, record: LessonHistory) =>
        !record.isRegistered ? (
          '-'
        ) : (
          <Label status={record.attendance}>{record.attendance === 'Absent' ? <>({text})</> : <>{text}</>}</Label>
        ),
    },
    {
      title: intl.formatMessage({ id: 'Goals' }),
      dataIndex: 'goals',
      key: 'goals',
      align: 'left',
      // eslint-disable-next-line react/display-name
      render: (_text: string, record: LessonHistory) => {
        const goalIsPending = record.goalCompleted === 'Pending' && record && record.goals
        const goalNotAchieved = record.goals
        const isProgramOnline = props.isProgramLesson({ Type: record.type } as GroupClassListItem)
        return !record.isRegistered ? (
          '-'
        ) : (
          <RecordWrapper>
            {record.goalCompleted === 'Completed' && record.attendance !== 'Absent' && (
              <Pill success spaceRight>
                <Tick />
              </Pill>
            )}
            {goalIsPending && record.attendance !== 'Absent' && (
              <>
                {isProgramOnline ? (
                  <Pill warning={isProgramOnline} spaceRight>
                    <Minus />
                  </Pill>
                ) : (
                  <Pill spaceRight>
                    <Clear />
                  </Pill>
                )}

                <div>
                  {goalNotAchieved.map((goal) => (
                    <Pill warning={isProgramOnline} key={goal} spaceRight>
                      <FormattedMessage id={goal} />
                    </Pill>
                  ))}
                </div>
              </>
            )}
          </RecordWrapper>
        )
      },
    },
    {
      title: intl.formatMessage({ id: 'Instructor' }),
      dataIndex: 'instructor',
      key: 'instructor',
      align: 'left',
      // eslint-disable-next-line react/display-name
      render: (text: string, record: LessonHistory) => <Label status={record.attendance}>{text}</Label>,
    },
  ]

  const columns = ahColView ? attendanceHomeCol : defaultColumns

  const buttonTitle = ahColView ? 'View material, goal & performance' : 'View attendance & homework'

  return (
    <Section>
      <Box>
        <Row>
          <Col xs={12} md={8} className="no-padding">
            <StyledH2>{intl.formatMessage({ id: title })}</StyledH2>
          </Col>
          <Col xs={12} md={4} className="no-padding">
            <ButtonContainer>
              {displayLessonsBtn && (
                <OutlineButton
                  outline={!showMyLessons}
                  onClick={() => {
                    const isShown = !showMyLessons
                    setShowMyLessons(isShown)

                    if (isShown) {
                      const name = [
                        get(instructor, 'InstructorProfile.instructorInfo.contactInfo.FirstName'),
                        get(instructor, 'InstructorProfile.instructorInfo.contactInfo.LastName'),
                      ].join(' ')

                      const lessons = lessonHistory.filter((lesson) => lesson.instructor === name)
                      setLessonHistory(lessons)
                    } else {
                      setLessonHistory()
                    }
                  }}
                >
                  <FormattedMessage id="My lessons" />
                </OutlineButton>
              )}

              <StyledButton
                onClick={() => {
                  setahColView(!ahColView)
                }}
              >
                <FormattedMessage id={buttonTitle} />
              </StyledButton>
            </ButtonContainer>
          </Col>
          {options.length > 1 && (
            <Col xs={12} md={2} className="no-padding">
              <SelectLevel>
                <StyledSelect
                  label=""
                  name="Student"
                  value={get(selectedStudent, 'CustomerId')}
                  onChange={(value) => {
                    const student = find(students, { CustomerId: value })
                    if (student) {
                      setStudent(student)
                    }
                  }}
                  options={options}
                  disableMargin
                />
              </SelectLevel>
            </Col>
          )}
        </Row>
        {loading && <TableLoader />}
        {!loading && <TableList columns={columns} lessonHistory={lessonHistory} />}
      </Box>
    </Section>
  )
}

export default WithLessonUtilsHOC(injectIntl(RecordTable))
